import { ApiSuccessSchema } from "../../../common/response.schema";
import {
  GetVibeEdgeTokenPathVarsSchema,
  GetVibeEdgeTokenResponseSchema,
  MintVibeAppSessionBodySchema,
  MintVibeAppSessionPathVarsSchema,
  MintVibeAppSessionResponseSchema,
  RotateVibeEdgeTokenPathVarsSchema,
  RotateVibeEdgeTokenResponseSchema,
  SetVibeAppVisibilityBodySchema,
  SetVibeAppVisibilityPathVarsSchema,
  SetVibeAppVisibilityResponseSchema
} from "../schemas";

export const VibeAppEdgeEndpoints = {
  // ---------------------------------------------------------------------------
  // Edge-auth token — the per-app secret the Traefik edge matches
  // ---------------------------------------------------------------------------

  /**
   * Reveal the app's edge-auth token — the shared secret the Traefik edge
   * matches (via `X-Vibe-App-Token`) before admitting a request. The response
   * carries the decrypted token plus the header name and the app's public URL,
   * so it is enough on its own to build a working test request. Org-scoped: the
   * `:id` addresses the app, the org scope comes from the JWT.
   *
   * 404 if the app is not in the caller's org. 409 if the app carries no token
   * (a transition-era app that deploys open). The `token` is a LIVE secret;
   * treat the whole payload as sensitive.
   */
  GetEdgeToken: {
    method: "GET" as const,
    path: "/api/vibe/apps/:id/edge-token",
    PathVars: GetVibeEdgeTokenPathVarsSchema,
    Response: ApiSuccessSchema(GetVibeEdgeTokenResponseSchema)
  },

  /**
   * Rotate the app's edge-auth token: mint a fresh secret, seal it, and swap
   * it in atomically (build-then-swap — the new token is persisted before the
   * old is forgotten), returning the new token in the same reveal-once shape.
   * The next reconcile updates the edge `Header()` match to the new token.
   *
   * 404 if the app is not in the caller's org. `toolResyncRequired` is `true`
   * when the app is registered as an agent tool — that tool's baked-in auth
   * still holds the OLD token and must be re-registered or it will 404 at the
   * edge.
   */
  RotateEdgeToken: {
    method: "POST" as const,
    path: "/api/vibe/apps/:id/edge-token/rotate",
    PathVars: RotateVibeEdgeTokenPathVarsSchema,
    Response: ApiSuccessSchema(RotateVibeEdgeTokenResponseSchema)
  },

  /**
   * Flip the app between PRIVATE and PUBLIC, moving the stored intent and the
   * edge-auth token together in one write.
   *
   * `→ PUBLIC` drops the token, so the edge stops asking for an identity and
   * anyone with the URL reaches the app. `→ PRIVATE` mints a fresh token, so the
   * edge again admits only a caller presenting it in `X-Vibe-App-Token` or
   * holding a session minted for this app. The response reveals the new secret
   * once, on `→ PRIVATE` only.
   *
   * **Neither direction changes the Traefik route, and neither un-serves the
   * app.** The router rule is a bare `Host()` with the ForwardAuth middleware
   * attached for both visibilities. What propagates is the tenant's edge-authz
   * table: the agent republishes each app's visibility and token digest on its
   * next reconcile pass, so the flip takes effect when that table lands rather
   * than instantly.
   *
   * **`→ PRIVATE` is therefore not immediate, and the gap is an exposure
   * window.** The edge keeps enforcing the PREVIOUS visibility until the new
   * table lands, so an app being closed stays reachable by URL to anyone who
   * has it until the next reconcile pass completes. A caller tightening
   * visibility must treat the app as still public until then, rather than
   * assume the write closed it.
   *
   * Idempotent: setting the visibility the app already has is a no-op and does
   * NOT churn the token — re-asserting PRIVATE must not silently invalidate a
   * linked tool's baked-in auth.
   *
   * 404 if the app is not in the caller's org. `toolResyncRequired` is `true`
   * only on `→ PRIVATE` with a linked tool, whose baked-in auth predates the new
   * token; `→ PUBLIC` never needs a resync, because the edge stops checking a
   * token at all and ignores the stale header.
   */
  SetAppVisibility: {
    method: "PATCH" as const,
    path: "/api/vibe/apps/:id/visibility",
    PathVars: SetVibeAppVisibilityPathVarsSchema,
    Body: SetVibeAppVisibilityBodySchema,
    Response: ApiSuccessSchema(SetVibeAppVisibilityResponseSchema)
  },

  /**
   * Mint a browser session for one app — the console half of the sign-in
   * handshake, and the ONLY place Vibe app access is actually evaluated.
   *
   * The tenant's edge authorizer holds no policy: it redirects an unauthenticated
   * browser here, and this route authenticates the person (Clerk, via the normal
   * console auth), runs the real `PermissionCheckerService.canAccess()` against
   * the app's ReBAC grants, and signs a short-lived app-scoped assertion. The
   * response is a URL on the APP's own host — a cookie cannot cross an origin, so
   * the console cannot set it and instead hands the browser somewhere that can.
   *
   * 403 when the caller has no grant, and that is deliberately distinct from 404
   * here: the caller is already authenticated into the owning org, so "you cannot
   * open this" is the useful answer. The EDGE never draws that distinction,
   * because there the caller may be anyone.
   *
   * 409 for an app with no public URL (never deployed — there is no host to set a
   * cookie on). 400 when `returnTo` does not belong to this app: the open-redirect
   * boundary, which refuses rather than silently rewriting.
   */
  MintAppSession: {
    method: "POST" as const,
    path: "/api/vibe/apps/:id/session",
    PathVars: MintVibeAppSessionPathVarsSchema,
    Body: MintVibeAppSessionBodySchema,
    Response: ApiSuccessSchema(MintVibeAppSessionResponseSchema)
  }
} as const;
